(() => {
  const knownVariants = new Set();
  const customVariants = new Map();
  const registerVariants = (identifiers) => identifiers.forEach((identifier) => {
    if (/^\d+$/.test(String(identifier))) knownVariants.add(String(identifier));
  });
  const storageKey = 'nfc-test-cart-pending';
  const quantityMessage = 'Enter a valid whole-number quantity.';
  const fileNamesFor = (count) => Array.from({ length: count }, (_, index) => `Image ${String(index + 1).padStart(2, '0')}`);
  const familyLabels = { record: 'Record player', cassette: 'Cassette player', rotating: 'Rotating music box', discs: 'Disc accessory set' };
  const printLayouts = ['Side Panel', 'Two Round Windows', 'Center Window', 'Full Artwork'];
  const printLayoutIds = ['side-panel', 'two-round-windows', 'center-window', 'full-artwork'];
  const registerCustomVariants = (variants) => variants.forEach((variant) => {
    if (variant && /^\d+$/.test(String(variant.id))) {
      const family = variant.family || 'record';
      const imageCount = variant.imageCount ?? (family === 'rotating' ? 1 : 9);
      if (!Object.hasOwn(familyLabels, family) || imageCount !== (family === 'rotating' ? 1 : 9) || (['rotating', 'discs'].includes(family) && variant.music)) throw new Error('Invalid custom product configuration.');
      knownVariants.add(String(variant.id));
      if (variant.printLayout && (family !== 'cassette' || !printLayouts.includes(variant.printLayout))) throw new Error('Invalid print layout configuration.');
      customVariants.set(String(variant.id), { music: !!variant.music, imageCount, family, printLayout: variant.printLayout || null });
    }
  });
  const customInputs = (body) => {
    if (!body || typeof body.get !== 'function' || typeof body.entries !== 'function') throw new Error('Your design could not be read.');
    const identifier = String(body.get('id') || '');
    const config = customVariants.get(identifier);
    if (!config) throw new Error('This custom option is unavailable.');
    const quantity = parseQuantity(body.get('quantity'));
    const designId = String(body.get('properties[Design ID]') || '');
    if (!/^[A-Za-z0-9-]{16,80}$/.test(designId)) throw new Error('A design reference is required.');
    const fileNames = fileNamesFor(config.imageCount);
    const playerType = String(body.get('properties[Player type]') || '');
    const customization = String(body.get('properties[Customization]') || '');
    const printLayout = String(body.get('properties[Print layout]') || '');
    if ((config.printLayout && printLayout !== config.printLayout) || (printLayout && (config.family !== 'cassette' || !printLayouts.includes(printLayout)))) throw new Error('Choose the matching cassette print layout before adding your design.');
    const cropMap = String(body.get('properties[Crop map]') || '');
    if (config.printLayout) {
      let placement;
      try { placement = JSON.parse(cropMap); } catch { throw new Error('Confirm all nine print compositions before adding your design.'); }
      const layoutId = printLayoutIds[printLayouts.indexOf(config.printLayout)];
      if (cropMap.length > 60000 || placement.family !== 'cassette' || placement.printLayout?.id !== layoutId || placement.printLayout?.label !== config.printLayout || !Array.isArray(placement.images) || placement.images.length !== 9 || placement.images.some((image, index) => image.n !== index + 1 || !image.printTransform || !['x', 'y', 'zoom'].every(key => Number.isFinite(image.printTransform[key])) || image.printTransform.x < 0 || image.printTransform.x > 1 || image.printTransform.y < 0 || image.printTransform.y > 1 || image.printTransform.zoom < 1 || image.printTransform.zoom > 8)) throw new Error('Your saved print positions do not match this layout.');
    }
    const expectedCustomization = config.family === 'rotating' ? '1 character portrait' : config.music ? '9 photos + 9 tracks' : '9 photos';
    if ((playerType && playerType !== familyLabels[config.family]) || (customization && customization !== expectedCustomization)) throw new Error('The design does not match this player.');
    const allowedKeys = new Set(['id', 'quantity', 'properties[Design ID]', 'properties[Preview]', 'properties[Crop map]', 'properties[Player type]', 'properties[Customization]', 'properties[Print layout]', ...fileNames.map(name => `properties[${name}]`), ...(config.music ? fileNamesFor(9).map((_, index) => `properties[Track ${String(index + 1).padStart(2, '0')}]`) : [])]);
    const seen = new Set();
    for (const [key] of body.entries()) {
      if (!allowedKeys.has(key) || seen.has(key)) throw new Error('The design contains unexpected or duplicate fields.');
      seen.add(key);
    }
    for (const name of [...fileNames, 'Preview']) {
      const file = body.get(`properties[${name}]`);
      if (!file || typeof file !== 'object' || file.size < 1 || file.size > 10 * 1024 * 1024 || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error(`Upload and crop ${config.imageCount === 1 ? 'your portrait' : 'all 9 photos'} before adding your design.`);
    }
    for (let number = 1; number <= 9; number += 1) {
      const key = `properties[Track ${String(number).padStart(2, '0')}]`;
      const value = String(body.get(key) || '').trim();
      if (config.music && (!value || value.length > 300)) throw new Error('Enter all 9 song titles and artists.');
      if (!config.music && value) throw new Error('Choose the music add-on to include your track list.');
    }
    const tracks = config.music ? fileNamesFor(9).map((_, index) => String(body.get(`properties[Track ${String(index + 1).padStart(2, '0')}]`)).trim()) : [];
    return { identifier, quantity, designId, music: config.music, imageCount: config.imageCount, family: config.family, playerType, customization, printLayout, cropMap: config.printLayout ? cropMap : null, tracks };
  };
  const storedFile = (value) => {
    if (typeof value !== 'string') return false;
    try {
      const url = new URL(value.startsWith('//') ? 'https:' + value : value);
      return url.protocol === 'https:' && (url.hostname === 'cdn.shopify.com' || url.hostname === 'files.shopifycdn.com') && url.pathname.length > 8;
    } catch { return false; }
  };
  const validateCustomLine = (line, requested) => {
    const properties = line?.properties || {};
    if (String(line?.variant_id ?? line?.id) !== requested.identifier || properties['Design ID'] !== requested.designId) throw new Error('Design reference did not match the saved cart item.');
    const fileNames = fileNamesFor(requested.imageCount || 9);
    if (![...fileNames, 'Preview'].every(name => storedFile(properties[name]))) throw new Error('Your photo uploads were not confirmed. Check your cart before retrying.');
    if (requested.music && !fileNames.every((_, index) => String(properties[`Track ${String(index + 1).padStart(2, '0')}`] || '').trim())) throw new Error('Your music list was not saved completely.');
    if (requested.tracks?.some((track, index) => String(properties[`Track ${String(index + 1).padStart(2, '0')}`] || '').trim() !== track)) throw new Error('Your saved track list does not match the design.');
    if ((requested.playerType && properties['Player type'] !== requested.playerType) || (requested.customization && properties.Customization !== requested.customization)) throw new Error('Your customization details were not saved.');
    if (requested.printLayout && properties['Print layout'] !== requested.printLayout) throw new Error('Your selected print layout was not saved.');
    if (requested.cropMap && properties['Crop map'] !== requested.cropMap) throw new Error('Your print positions were not saved.');
    return line;
  };

  function parseQuantity(value, rules = {}, allowZero = false) {
    const raw = String(value ?? '').trim();
    if (!/^\d+$/.test(raw)) throw new Error(quantityMessage);
    const quantity = Number(raw);
    const minimum = Math.max(1, Number(rules.min || 1));
    const increment = Number(rules.increment || 1);
    const maximum = rules.max === '' || rules.max === null || rules.max === undefined ? null : Number(rules.max);
    if (allowZero && quantity === 0) return 0;
    if (!Number.isSafeInteger(quantity) || !Number.isSafeInteger(minimum) || !Number.isSafeInteger(increment) || increment < 1 || quantity < minimum || (quantity - minimum) % increment !== 0 || (maximum !== null && (!Number.isSafeInteger(maximum) || quantity > maximum))) throw new Error(quantityMessage);
    return quantity;
  }

  function validateCart(cart) {
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || cart.currency !== 'USD') throw new Error('The cart response was incomplete.');
    let count = 0;
    const keys = new Set();
    for (const item of cart.items) {
      if (typeof item.key !== 'string' || !item.key || keys.has(item.key) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !Number.isSafeInteger(item.final_price) || item.final_price < 0 || !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0 || !/^\d+$/.test(String(item.variant_id))) throw new Error('The cart response was incomplete.');
      count += item.quantity;
      keys.add(item.key);
    }
    if (cart.item_count !== count) throw new Error('The cart response was inconsistent.');
    return cart;
  }

  function createClient({ fetchImpl, origin, routes, storage, onState = () => {} }) {
    let pending = false;
    let uncertain = false;
    let knownKeys = new Set();
    try { uncertain = storage?.getItem(storageKey) === 'unconfirmed'; } catch {}
    const persist = (value) => { try { if (value) storage?.setItem(storageKey, 'unconfirmed'); else storage?.removeItem(storageKey); } catch {} };
    const state = () => ({ pending, uncertain, locked: pending || uncertain });
    const notify = () => onState(state());
    const markUncertain = () => { uncertain = true; persist(true); notify(); };
    const endpoint = (kind) => {
      const suffix = kind === 'read' ? routes.cartUrl : kind === 'add' ? routes.cartAddUrl : routes.cartChangeUrl;
      const url = new URL(`${suffix}.js`, origin);
      if (url.origin !== origin || !(kind === 'read' ? /\/cart\.js$/ : kind === 'add' ? /\/cart\/add\.js$/ : /\/cart\/change\.js$/).test(url.pathname) || url.search || url.hash) throw new Error('The cart route is unavailable.');
      return url.href;
    };
    const acceptCart = (cart) => {
      validateCart(cart);
      knownKeys = new Set(cart.items.filter((item) => knownVariants.has(String(item.variant_id))).map((item) => item.key));
      return cart;
    };
    const read = async () => {
      const response = await fetchImpl(endpoint('read'), { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error('Could not read the current cart.');
      return acceptCart(await response.json());
    };
    async function mutate(kind, body) {
      if (pending || uncertain) return { kind: 'blocked' };
      let requestedCustom;
      if (kind === 'add') {
        if (!Array.isArray(body.items) || body.items.length !== 1 || !knownVariants.has(String(body.items[0].id))) return { kind: 'rejected', message: 'This option is currently unavailable. Please choose another option.' };
        if (customVariants.has(String(body.items[0].id))) return { kind: 'rejected', message: 'Complete your custom design first.' };
        try { parseQuantity(body.items[0].quantity); } catch (error) { return { kind: 'rejected', message: error.message }; }
      } else if (kind === 'custom') {
        try { requestedCustom = customInputs(body); } catch (error) { return { kind: 'rejected', message: error.message }; }
      } else if (kind === 'change') {
        if (!knownKeys.has(body.id)) return { kind: 'rejected', message: 'This item could not be found in your cart. Refresh your cart and try again.' };
        try { parseQuantity(body.quantity, {}, true); } catch (error) { return { kind: 'rejected', message: error.message }; }
      } else return { kind: 'rejected', message: 'Unsupported cart operation.' };
      let destination;
      try { destination = endpoint(kind === 'custom' ? 'add' : kind); } catch (error) { return { kind: 'rejected', message: error.message }; }
      pending = true;
      persist(true);
      notify();
      try {
        const multipart = kind === 'custom';
        const response = await fetchImpl(destination, { method: 'POST', credentials: 'same-origin', headers: multipart ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' }, body: multipart ? body : JSON.stringify(body), signal: AbortSignal.timeout(multipart ? 90000 : 15000) });
        const result = await response.json();
        if ([400, 401, 403, 404, 422].includes(response.status)) {
          persist(false);
          return { kind: 'rejected', message: typeof result.description === 'string' ? result.description : 'This change could not be completed. Review the message and try again.' };
        }
        if (!response.ok || result.status >= 400) throw new Error('Unconfirmed cart response.');
        let cart;
        if (kind === 'custom') {
          const line = Array.isArray(result.items) ? result.items.find(item => item.properties?.['Design ID'] === requestedCustom.designId) : result;
          validateCustomLine(line, requestedCustom);
          cart = await read();
          validateCustomLine(cart.items.find(item => item.properties?.['Design ID'] === requestedCustom.designId), requestedCustom);
        } else if (kind === 'add') {
          if (!Array.isArray(result.items) || result.items.length !== 1 || String(result.items[0].variant_id ?? result.items[0].id) !== String(body.items[0].id) || !Number.isSafeInteger(result.items[0].quantity) || result.items[0].quantity < 1) throw new Error('Incomplete add response.');
          cart = await read();
        } else cart = acceptCart(result);
        persist(false);
        return { kind: 'success', cart };
      } catch {
        uncertain = true;
        persist(true);
        return { kind: 'uncertain', message: 'We could not confirm this change. Check your cart before trying again.' };
      } finally { pending = false; notify(); }
    }
    async function reconcile() {
      if (pending) return { kind: 'blocked' };
      pending = true;
      notify();
      try {
        const cart = await read();
        uncertain = false;
        persist(false);
        return { kind: 'success', cart };
      } catch {
        uncertain = true;
        persist(true);
        return { kind: 'uncertain', message: 'The cart could not be checked. No change was retried.' };
      } finally { pending = false; notify(); }
    }
    return { state, mutate, reconcile, markUncertain, seedKeys: (keys) => { knownKeys = new Set(keys); } };
  }

  const exported = { registerVariants, registerCustomVariants, knownVariants, customInputs, validateCustomLine, storedFile, parseQuantity, validateCart, createClient };
  if (typeof module !== 'undefined' && module.exports) module.exports = exported;
  else globalThis.NFCCart = exported;
})();
